import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useStore();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      if (polls.current >= 6) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order); setState("success");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="checkout-success-page">
      {state === "checking" && <><Loader2 className="h-12 w-12 mx-auto animate-spin text-[#D4AF37]" /><p className="mt-6 font-display text-2xl">Confirming your payment…</p></>}
      {state === "success" && (
        <>
          <CheckCircle2 className="h-16 w-16 mx-auto text-[#D4AF37]" />
          <h1 className="font-display text-4xl mt-6">Thank you!</h1>
          <p className="text-muted-foreground mt-3">Your order is confirmed. A confirmation has been sent to your email.</p>
          {order && (
            <div className="bg-[#F5F1E8] dark:bg-[#141414] p-6 mt-8 text-left">
              <p className="text-sm text-muted-foreground">Tracking number</p>
              <p className="font-display text-2xl" data-testid="tracking-number">{order.tracking_number}</p>
              <p className="mt-3 flex justify-between"><span>Total paid</span><strong>{formatPrice(order.total)}</strong></p>
            </div>
          )}
          <div className="flex gap-3 justify-center mt-8">
            <Link to={`/track-order?t=${order?.tracking_number || ""}`}><Button className="rounded-none bg-[#D4AF37] text-black" data-testid="track-order-btn">Track Order</Button></Link>
            <Link to="/shop"><Button variant="outline" className="rounded-none border-black dark:border-white">Keep Shopping</Button></Link>
          </div>
        </>
      )}
      {(state === "error" || state === "expired" || state === "timeout") && (
        <>
          <XCircle className="h-16 w-16 mx-auto text-destructive" />
          <h1 className="font-display text-3xl mt-6">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-muted-foreground mt-3">{state === "timeout" ? "Your payment is taking longer than usual. Check your email for confirmation." : "We couldn't confirm your payment."}</p>
          <Link to="/cart"><Button className="mt-8 rounded-none bg-[#D4AF37] text-black">Back to Cart</Button></Link>
        </>
      )}
    </div>
  );
}
